import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ReactSVG } from "react-svg";
import { useTranslation } from "react-i18next";
import { Button as ButtonLib, Tooltip as TooltipLib } from "@npm_leadtech/qr-ui-components";
import { Input } from "../Input";
import { Typography } from "../Typography";
import ListPickerItem from "./ListPickerItem";
import { ConnectForm, URLRegex } from "@/utils/formHelper";
import { ListPickerProps } from "./types";
import { FormValidationMethods } from "../Form/types";

import { NAMESPACES } from "@/internals/constants/namespaces";
import "./styles.scss";

export function ListPicker({
  dataQA,
  title,
  titleEmpty,
  defaultValue,
  options,
  onClick,
  extended,
}: ListPickerProps) {
  const methods: FormValidationMethods = useFormContext();
  const [selectedOptions, setSelectedOptions] = useState(defaultValue || []);
  const { t: tValidations } = useTranslation(NAMESPACES.VALIDATIONS);

  const addRemoveOption = (item) => {
    const index = selectedOptions.findIndex((opt) => opt.id === item.id);

    if (index !== -1) {
      const filteredArray = selectedOptions.filter((option) => option.id !== item.id);
      setSelectedOptions(filteredArray);
    } else {
      setSelectedOptions([...selectedOptions, item]);
    }
  };

  const onChangeValue = (item, inputType?: "text" | "url") => {
    const { id } = item;
    const targetIndex = selectedOptions.findIndex((opt) => opt.id === id);

    if (targetIndex === -1) return;

    const newSelectedSocials = selectedOptions.map((option, index) => {
      if (index === targetIndex) {
        if (inputType) {
          const inputName = inputType === "url" ? id : `${id}-${inputType}`;
          return {
            ...option,
            [inputType]: {
              ...option[inputType],
              value: methods?.getValues(inputName),
            },
          };
        } else {
          return {
            ...option,
            value: methods?.getValues(id),
          };
        }
      }
      return option;
    });

    setSelectedOptions(newSelectedSocials);
  };

  useEffect(() => {
    if (onClick && defaultValue) {
      setSelectedOptions(defaultValue);
      onClick(selectedOptions);
    }
  }, []);

  useEffect(() => {
    if (onClick) {
      onClick(selectedOptions);
    }
  }, [selectedOptions]);

  return (
    <ConnectForm>
      <div
        data-testid="ListPicker"
        data-qa={dataQA}
        className="ListPicker"
      >
        <div>
          {selectedOptions.map((item) => {
            if (extended) {
              const { url, text } = item;

              return (
                <div
                  className="ListPicker__social-wrapper"
                  key={item.id}
                >
                  <div className="ListPicker__social-name">
                    <ReactSVG
                      className="ListPicker__icon"
                      src={`/svg/icon-${item.id}.svg`}
                    />
                    <span className="ListPicker__label">{item.name}</span>
                  </div>

                  <ButtonLib
                    id="picker-remove-button"
                    variant="error"
                    shape="ghost"
                    ariaLabel="Remove item"
                    data-testid="ListPickerRemoveButton"
                    dataQA="ListPickerRemoveButton"
                    onClick={() => addRemoveOption(item)}
                    iconLeft={
                      <ReactSVG
                        src={`/svg/icon-remove-circle-outline.svg`}
                        strokeWidth={2}
                      />
                    }
                  />

                  <div className="ListPicker__input-wrapper--extended">
                    <>
                      <span className="ListPicker__label">{url.label}</span>
                      <Controller
                        control={methods?.control}
                        name={item.id}
                        defaultValue={url.value || ""}
                        render={({ field }) => (
                          <Input
                            id={item.id}
                            type="text"
                            {...field}
                            name={item.id}
                            onChange={() => {
                              onChangeValue(item, "url");
                            }}
                            error={methods?.formState?.errors[item.id]}
                            errorMessage={methods?.formState?.errors[item.id]?.message}
                            noMb
                            placeholder={url.placeholder}
                            dataQA={`input-${item?.id}`}
                          />
                        )}
                        rules={{
                          required: {
                            value: true,
                            message: tValidations("required_message"),
                          },
                          pattern: {
                            value: new RegExp(URLRegex),
                            message: tValidations("social_invalid"),
                          },
                        }}
                      />
                    </>

                    <>
                      <span className="ListPicker__label">{text.label}</span>

                      <Controller
                        control={methods?.control}
                        name={`${item.id}-text`}
                        defaultValue={text.value || ""}
                        render={({ field }) => (
                          <Input
                            id={`${item.id}-text`}
                            type="text"
                            {...field}
                            name={`${item.id}-text`}
                            onChange={() => {
                              onChangeValue(item, "text");
                            }}
                            error={methods?.formState?.errors[`${item.id}-text`]}
                            errorMessage={methods?.formState?.errors[`${item.id}-text`]?.message}
                            noMb
                            placeholder={text.placeholder}
                            dataQA={`text-${item?.id}`}
                          />
                        )}
                        rules={{
                          maxLength: {
                            value: 70,
                            message: tValidations("max_length"),
                          },
                        }}
                      />
                    </>
                  </div>
                </div>
              );
            }

            return (
              <div
                className="ListPicker__social-wrapper"
                key={item.id}
              >
                <div className="ListPicker__social-name">
                  <ReactSVG
                    className="ListPicker__icon"
                    src={`/svg/icon-${item.id}.svg`}
                  />
                  <span className="ListPicker__label">{item.name}</span>
                </div>

                <ButtonLib
                  id="picker-remove-button"
                  variant="error"
                  shape="ghost"
                  ariaLabel="Remove item"
                  data-testid="ListPickerRemoveButton"
                  dataQA="ListPickerRemoveButton"
                  onClick={() => addRemoveOption(item)}
                  iconLeft={
                    <ReactSVG
                      src={`/svg/icon-remove-circle-outline.svg`}
                      strokeWidth={2}
                    />
                  }
                />

                <div className="ListPicker__input-wrapper">
                  <Input
                    id={item.id}
                    type="text"
                    name={item.id}
                    defaultValue={item?.value || ""}
                    onChange={() => onClick && onChangeValue(item)}
                    error={methods?.formState?.errors[item.id]}
                    errorMessage={methods?.formState?.errors[item.id]?.message}
                    noMb
                    placeholder={item.placeholder}
                    dataQA={`input-${item?.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Typography
          classname="u-bold"
          Tag={"p"}
        >
          {selectedOptions.length ? titleEmpty : title}
        </Typography>

        <ul
          data-testid="ListPicker__list"
          className="ListPicker__list"
        >
          {options.map((item, index) => (
            <React.Fragment key={index}>
              {item.tooltip ? (
                <TooltipLib
                  dataQA="list-picker-tooltip"
                  dataTestId="tooltip"
                  position={item.tooltip?.position}
                  text={item.tooltip?.text}
                >
                  <ListPickerItem
                    item={item}
                    selectedOptions={selectedOptions}
                    addRemoveOption={addRemoveOption}
                    id={index}
                  />
                </TooltipLib>
              ) : (
                <ListPickerItem
                  item={item}
                  selectedOptions={selectedOptions}
                  addRemoveOption={addRemoveOption}
                  id={index}
                />
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>
    </ConnectForm>
  );
}
